import { useMemo } from 'react'
import {
  useMinigamesServiceGetAllCampaignsQuery,
  useMinigamesServiceGetGameTemplatesQuery,
} from 'zma-react-sdk/minigames-service'
import { useTenantStore } from '@/stores/tenantStore'
import {
  Campaign,
  GameTemplate,
  MinigamesServiceCampaignOutput,
  MinigamesServiceGameTemplateOutput,
} from '../types'

// Transform API data to UI friendly format
function transformCampaignData(
  apiData: MinigamesServiceCampaignOutput[],
  templates: MinigamesServiceGameTemplateOutput[]
): Campaign[] {
  return apiData.map((campaign) => {
    const template = templates?.find((t) => t.id === campaign.templateId)

    return {
      id: campaign.id,
      name: campaign.name,
      description: campaign.description ?? undefined,
      status: campaign.status as any,
      startDate: campaign.startDate,
      endDate: campaign.endDate,
      gameType: template?.gameType as any,
      templateName: template?.templateName,
      maxPlaysPerUser: campaign.maxPlaysPerUser,
      authenticationRequired: campaign.authenticationRequired,
      playCodeRequired: campaign.playCodeRequired,
      createdAt: campaign.createdAt,
      updatedAt: campaign.updatedAt,
      createdBy: campaign.createdBy,
      participantsCount: 0, // TODO: Calculate from game records
    }
  })
}

interface UseCampaignsOptions {
  limit?: number
  offset?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  status?: string
  gameType?: string
  search?: string
}

export function useCampaigns(options: UseCampaignsOptions = {}) {
  const {
    limit = 10,
    offset = 0,
    sortBy,
    sortOrder = 'desc',
    status,
    gameType,
    search,
  } = options
  const { selectedTenantId } = useTenantStore()

  const { data, loading, error, refetch } =
    useMinigamesServiceGetAllCampaignsQuery({
      variables: {
        pagination: {
          limit,
        },
      },
      context: {
        headers: {
          'x-tenant-id': selectedTenantId,
        },
      },
      skip: !selectedTenantId,
    })

  const {
    data: templatesData,
    loading: templatesLoading,
    error: templatesError,
  } = useMinigamesServiceGetGameTemplatesQuery({
    context: {
      headers: {
        'x-tenant-id': selectedTenantId,
      },
    },
    skip: !selectedTenantId,
  })

  const campaigns = useMemo(() => {
    if (!data?.minigamesServiceGetAllCampaigns?.data) {
      return []
    }

    // If templates not loaded yet, return empty array to avoid crashes
    if (!templatesData?.minigamesServiceGetGameTemplates) {
      return []
    }

    // Transform data
    let allCampaigns = transformCampaignData(
      data.minigamesServiceGetAllCampaigns.data,
      templatesData.minigamesServiceGetGameTemplates
    )

    // Apply filters
    if (search) {
      const searchLower = search.toLowerCase()
      allCampaigns = allCampaigns.filter(
        (campaign) =>
          campaign.name.toLowerCase().includes(searchLower) ||
          campaign.description?.toLowerCase().includes(searchLower)
      )
    }

    if (status) {
      allCampaigns = allCampaigns.filter(
        (campaign) => campaign.status === status
      )
    }

    if (gameType) {
      allCampaigns = allCampaigns.filter(
        (campaign) => campaign.gameType === gameType
      )
    }

    // Apply sorting
    if (sortBy) {
      allCampaigns = [...allCampaigns].sort((a, b) => {
        let aVal = (a as any)[sortBy]
        let bVal = (b as any)[sortBy]

        // Handle date strings
        if (
          sortBy.includes('Date') ||
          sortBy === 'createdAt' ||
          sortBy === 'updatedAt'
        ) {
          aVal = new Date(aVal).getTime()
          bVal = new Date(bVal).getTime()
        }

        // Handle strings
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          aVal = aVal.toLowerCase()
          bVal = bVal.toLowerCase()
        }

        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
        } else {
          return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
        }
      })
    }

    // Apply client-side pagination
    return allCampaigns.slice(offset, offset + limit)
  }, [
    data,
    templatesData,
    offset,
    limit,
    search,
    status,
    gameType,
    sortBy,
    sortOrder,
  ])

  const total = useMemo(() => {
    if (!data?.minigamesServiceGetAllCampaigns?.data) return 0

    // Count after filtering but before pagination
    let allCampaigns = data.minigamesServiceGetAllCampaigns.data

    if (search) {
      const searchLower = search.toLowerCase()
      allCampaigns = allCampaigns.filter(
        (campaign) =>
          campaign.name.toLowerCase().includes(searchLower) ||
          campaign.description?.toLowerCase().includes(searchLower)
      )
    }

    if (status) {
      allCampaigns = allCampaigns.filter(
        (campaign) => campaign.status === status
      )
    }

    if (gameType && templatesData?.minigamesServiceGetGameTemplates) {
      allCampaigns = allCampaigns.filter((campaign) => {
        const template = templatesData.minigamesServiceGetGameTemplates.find(
          (t) => t.id === campaign.templateId
        )
        return template?.gameType === gameType
      })
    }

    return allCampaigns.length
  }, [data, templatesData, search, status, gameType])

  return {
    campaigns,
    total,
    loading: loading || templatesLoading,
    error: error || templatesError,
    refetch,
  }
}

export function useActiveCampaigns() {
  const { selectedTenantId } = useTenantStore()

  const { data, loading, error } = useMinigamesServiceGetAllCampaignsQuery({
    variables: {
      pagination: {
        limit: 100,
      },
    },
    context: {
      headers: {
        'x-tenant-id': selectedTenantId,
      },
    },
    skip: !selectedTenantId,
  })

  const { data: templatesData, loading: templatesLoading } =
    useMinigamesServiceGetGameTemplatesQuery({
      context: {
        headers: {
          'x-tenant-id': selectedTenantId,
        },
      },
      skip: !selectedTenantId,
    })

  const activeCampaigns = useMemo(() => {
    if (
      !data?.minigamesServiceGetAllCampaigns?.data ||
      !templatesData?.minigamesServiceGetGameTemplates
    ) {
      return []
    }

    return transformCampaignData(
      data.minigamesServiceGetAllCampaigns.data,
      templatesData?.minigamesServiceGetGameTemplates
    )
  }, [data, templatesData])

  return {
    campaigns: activeCampaigns,
    loading: loading || templatesLoading,
    error,
  }
}

export function useGameTemplates() {
  const { selectedTenantId } = useTenantStore()

  const { data, loading, error } = useMinigamesServiceGetGameTemplatesQuery({
    context: {
      headers: {
        'x-tenant-id': selectedTenantId,
      },
    },
    skip: !selectedTenantId,
  })

  const templates: GameTemplate[] = useMemo(() => {
    if (!data?.minigamesServiceGetGameTemplates) {
      return []
    }

    return data.minigamesServiceGetGameTemplates.map(
      (template: MinigamesServiceGameTemplateOutput) => ({
        id: template.id,
        templateName: template.templateName,
        gameType: template.gameType as any,
        isActive: template.isActive,
        defaultGameConfig: template.defaultGameConfig,
        defaultUiConfig: template.defaultUiConfig,
        createdAt: template.createdAt,
        updatedAt: template.updatedAt,
      })
    )
  }, [data])

  return {
    templates,
    loading,
    error,
  }
}
